<template>
    <div class="exam-seats-management">
        <!-- 筛选器与概览 -->
        <el-card class="filter-card">
            <el-form :inline="true" class="filter-form">
                <el-form-item label="学期">
                    <el-select v-model="semesterId" placeholder="选择学期" @change="handleSemesterChange" style="width: 220px">
                        <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="考试">
                    <el-select v-model="examId" placeholder="选择考试" clearable @change="handleExamChange" style="width: 280px" :disabled="!semesterId">
                        <el-option v-for="e in exams" :key="e.id" :label="e.name" :value="e.id" />
                    </el-select>
                </el-form-item>
                <el-form-item v-if="examId" class="export-actions">
                    <el-button type="primary" :icon="Upload" @click="showImportDialog" style="margin-right: 8px">
                        导入学生名单
                    </el-button>
                    <el-button type="success" :icon="Cpu" @click="startArrangeAll" :loading="arrangingAll" style="margin-right: 12px">
                        一键编排所有场次
                    </el-button>
                    <el-button-group>
                        <el-button type="success" :icon="Download" @click="exportSeatingCharts" :loading="exporting">
                            导出考场座位图与签到表
                        </el-button>
                        <el-button type="warning" :icon="Download" @click="exportClassSeating" :loading="exporting">
                            导出班级座位查询表
                        </el-button>
                    </el-button-group>
                </el-form-item>
            </el-form>
        </el-card>

        <div v-if="examId" class="workspace-container">
            <!-- 左侧场次列表 -->
            <div class="sessions-aside">
                <el-card class="aside-card" shadow="never">
                    <template #header>
                        <div class="aside-header">
                            <span>考试场次列表</span>
                        </div>
                    </template>
                    <div class="sessions-list">
                        <div 
                            v-for="session in sessions" 
                            :key="session.id" 
                            class="session-item"
                            :class="{ 'is-active': selectedSession?.id === session.id }"
                            @click="selectSession(session)"
                        >
                            <div class="session-info">
                                <div class="subj-grade">
                                    <span class="session-grade">{{ session.grade }}</span>
                                    <span class="session-subject">{{ session.subject_name }}</span>
                                </div>
                                <div class="session-time">
                                    {{ session.exam_date }} {{ session.start_time.substring(0, 5) }}-{{ session.end_time.substring(0, 5) }}
                                </div>
                            </div>
                            <div class="session-status">
                                <el-tag v-if="session.assigned_count > 0" type="success" size="small">
                                    已排座 ({{ session.assigned_count }}人)
                                </el-tag>
                                <el-tag v-else type="info" size="small">未排座</el-tag>
                            </div>
                        </div>
                    </div>
                </el-card>
            </div>

            <!-- 右侧工作面板 -->
            <div class="workspace-main">
                <el-card v-if="selectedSession" class="main-card" shadow="never">
                    <template #header>
                        <div class="main-header">
                            <div class="selected-title">
                                <span class="grade-subj">{{ selectedSession.grade }} {{ selectedSession.subject_name }}</span>
                                <span class="date-time">
                                    ({{ selectedSession.exam_date }} {{ selectedSession.start_time.substring(0, 5) }}-{{ selectedSession.end_time.substring(0, 5) }})
                                </span>
                            </div>
                        </div>
                    </template>

                    <el-tabs v-model="activeTab" @tab-change="handleTabChange" class="workspace-tabs">
                        <!-- Tab 1: 考场容量与类型配置 -->
                        <el-tab-pane label="考场与容量配置" name="config">
                            <div class="pane-content">
                                <el-alert 
                                    title="设置说明" 
                                    type="info" 
                                    description="在此处可灵活微调各考场在当前场次中的最大容量和类型。若某些考场仅用于等级考或合格考，可将其类型更改为对应类别，系统在排考时会自动将不同需求的学生分流到对应考场中。"
                                    show-icon 
                                    :closable="false"
                                    class="config-alert"
                                />

                                <el-table :data="roomConfigs" stripe v-loading="configLoading">
                                    <el-table-column prop="room_name" label="考场名称" width="150" />
                                    <el-table-column prop="building" label="所在教学楼" width="150" />
                                    <el-table-column label="座位容量 (人)">
                                        <template #default="{ row }">
                                            <el-input-number 
                                                v-model="row.seat_capacity" 
                                                :min="1" 
                                                :max="200" 
                                                size="small"
                                            />
                                            <span class="default-cap-hint">默认: {{ row.default_capacity }}人</span>
                                        </template>
                                    </el-table-column>

                                    <el-table-column prop="assigned_count" label="已分学生" width="100">
                                        <template #default="{ row }">
                                            <el-tag :type="row.assigned_count > 0 ? 'success' : 'info'" size="small">
                                                {{ row.assigned_count }} 人
                                            </el-tag>
                                        </template>
                                    </el-table-column>
                                </el-table>

                                <div class="action-footer">
                                    <el-button type="primary" @click="saveRoomConfigs" :loading="savingConfigs">
                                        保存考场配置
                                    </el-button>
                                </div>
                            </div>
                        </el-tab-pane>

                        <!-- Tab 2: 一键自动排座 -->
                        <el-tab-pane label="一键自动排座" name="arrange">
                            <div class="pane-content arrange-pane">
                                <div class="stats-grid">
                                    <el-card shadow="hover" class="stat-box">
                                        <div class="stat-num text-primary">{{ stats.total_students }}</div>
                                        <div class="stat-label">参考学生总数</div>
                                    </el-card>
                                    <el-card shadow="hover" class="stat-box">
                                        <div class="stat-num text-success">{{ selectedSession.assigned_count }}</div>
                                        <div class="stat-label">已编排座位数</div>
                                    </el-card>
                                    <el-card shadow="hover" class="stat-box">
                                        <div class="stat-num text-warning">{{ stats.unassigned_count }}</div>
                                        <div class="stat-label">未编排学生数</div>
                                    </el-card>
                                    <el-card shadow="hover" class="stat-box">
                                        <div class="stat-num text-info">{{ stats.total_capacity }}</div>
                                        <div class="stat-label">总考场容量</div>
                                    </el-card>
                                </div>

                                <div class="arrange-actions">
                                    <div class="arrangement-selector">
                                        <span class="arrangement-label">座位编排方式</span>
                                        <el-radio-group v-model="arrangementMode" size="small">
                                            <el-radio-button label="vertical_snake">竖向蛇形</el-radio-button>
                                            <el-radio-button label="vertical">从前到后竖排</el-radio-button>
                                        </el-radio-group>
                                        <span class="arrangement-hint">从{{ arrangementStart === 'right' ? '右' : '左' }}上角开始，按列向下编号</span>
                                    </div>
                                    <div class="arrangement-selector">
                                        <span class="arrangement-label">1号起始位置</span>
                                        <el-radio-group v-model="arrangementStart" size="small">
                                            <el-radio-button label="right">右上角</el-radio-button>
                                            <el-radio-button label="left">左上角</el-radio-button>
                                        </el-radio-group>
                                        <span class="arrangement-hint">默认按进门位置设置为右上角</span>
                                    </div>
                                    <el-button 
                                        type="primary" 
                                        size="large" 
                                        class="huge-btn shadow-btn" 
                                        @click="startArrange" 
                                        :loading="arranging"
                                    >
                                        <el-icon><Cpu /></el-icon> 一键编排当前场次
                                    </el-button>
                                    <el-button 
                                        v-if="selectedSession.assigned_count > 0" 
                                        type="danger" 
                                        size="large" 
                                        plain
                                        @click="confirmClearSeating" 
                                    >
                                        清除当前场次排座
                                    </el-button>
                                    <el-button 
                                        v-if="selectedSession.assigned_count > 0" 
                                        type="warning" 
                                        size="large" 
                                        plain
                                        @click="showReplicateDialog"
                                    >
                                        复用座位到其他场次
                                    </el-button>
                                </div>

                                <!-- 未成功分配提示 -->
                                <div v-if="stats.unassigned_count > 0" class="warning-section">
                                    <el-alert 
                                        title="考位不足警示" 
                                        type="warning" 
                                        :description="`有 ${stats.unassigned_count} 名学生未分配到考场。请检查总容量或等级考/合格考考场的分配上限！`"
                                        show-icon 
                                        :closable="false"
                                        class="unassigned-alert"
                                    />
                                    <div class="unassigned-table-title">未分配学生名单：</div>
                                    <el-table :data="stats.unassigned_students" max-height="250" size="small" stripe>
                                        <el-table-column prop="student_id" label="学号" width="150" />
                                        <el-table-column prop="name" label="姓名" width="120" />
                                        <el-table-column prop="class_name" label="行政班级" />
                                        <el-table-column prop="selected_subjects" label="选科科目" />
                                    </el-table>
                                </div>
                            </div>
                        </el-tab-pane>

                        <!-- Tab 3: 座位表与预览 -->
                        <el-tab-pane label="座位表预览" name="preview">
                            <div class="pane-content">
                                <div class="preview-toolbar">
                                    <el-radio-group v-model="previewMode" size="small">
                                        <el-radio-button label="room">按考场查看</el-radio-button>
                                        <el-radio-button label="class">按班级查看</el-radio-button>
                                    </el-radio-group>

                                    <!-- 考场下拉选择 -->
                                    <el-select 
                                        v-if="previewMode === 'room'" 
                                        v-model="previewRoomId" 
                                        placeholder="选择考场" 
                                        size="small" 
                                        style="width: 200px; margin-left: 16px;"
                                        @change="loadRoomSeatMap"
                                    >
                                        <el-option 
                                            v-for="room in roomConfigs" 
                                            :key="room.exam_room_id" 
                                            :label="room.room_name" 
                                            :value="room.exam_room_id" 
                                        />
                                    </el-select>

                                    <!-- 班级下拉选择 -->
                                    <el-select 
                                        v-if="previewMode === 'class'" 
                                        v-model="previewClassName" 
                                        placeholder="选择班级" 
                                        size="small" 
                                        style="width: 200px; margin-left: 16px;"
                                        @change="loadClassSeatList"
                                    >
                                        <el-option v-for="c in availableClasses" :key="c" :label="c" :value="c" />
                                    </el-select>
                                </div>

                                <!-- 考场座位视图 -->
                                <div v-if="previewMode === 'room'" class="room-seating-view" v-loading="previewLoading">
                                    <div v-if="previewRoomId" class="seating-grid-container">
                                        <div class="blackboard">讲台方向 (屏幕)</div>
                                        
                                        <div class="seating-grid">
                                            <div 
                                                v-for="seat in seatMap" 
                                                :key="seat.seat_number" 
                                                class="seat-desk"
                                                :class="[getSeatClass(seat), { 'is-empty': !seat.student_name }]"
                                            >
                                                <div class="seat-badge">{{ seat.seat_number }}</div>
                                                <div class="seat-body" v-if="seat.student_name">
                                                    <div class="student-name">{{ seat.student_name }}</div>
                                                    <div class="student-class">{{ seat.class_name || '-' }}</div>
                                                </div>
                                                <div class="seat-body empty-body" v-else>
                                                    空座位
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <el-empty v-else description="请选择一个考场以加载座位表" />
                                </div>

                                <!-- 班级对照视图 -->
                                <div v-if="previewMode === 'class'" class="class-seating-view" v-loading="previewLoading">
                                    <div v-if="previewClassName" class="class-table-container">
                                        <el-table :data="classSeatingList" stripe size="small">
                                            <el-table-column prop="student_no" label="学号" width="150" />
                                            <el-table-column prop="student_name" label="姓名" width="120" />
                                            <el-table-column prop="building" label="教学楼" />
                                            <el-table-column prop="room_name" label="分配考场" />
                                            <el-table-column prop="seat_number" label="座位号" width="120">
                                                <template #default="{ row }">
                                                    <el-tag type="primary" size="small">{{ row.seat_number }} 号</el-tag>
                                                </template>
                                            </el-table-column>
                                        </el-table>
                                    </div>
                                    <el-empty v-else description="请选择一个班级以加载学生座位对照表" />
                                </div>
                            </div>
                        </el-tab-pane>
                    </el-tabs>
                </el-card>
                <el-empty v-else description="请在左侧选择一个考试场次进行座位编排" />
            </div>
        </div>
        <el-empty v-else description="请先在顶部选择学期和考试" />

        <!-- 导入学生名单对话框 -->
        <el-dialog v-model="importDialogVisible" title="导入学生名单" width="500px" destroy-on-close>
            <div class="import-body">
                <el-form label-position="top">
                    <el-form-item label="导入年级" required>
                        <el-select v-model="importForm.grade" placeholder="请选择年级" style="width: 100%">
                            <el-option v-for="g in availableGrades" :key="g" :label="g" :value="g" />
                        </el-select>
                    </el-form-item>
                </el-form>
                <div style="margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center;">
                    <span style="font-size: 13px; color: #475569;">选择导入模板并上传名单：</span>
                    <el-button type="primary" plain size="small" :icon="Download" @click="downloadStudentTemplate">
                        下载导入模板 (.xlsx)
                    </el-button>
                </div>
                <el-upload
                    ref="importUploadRef"
                    :auto-upload="false"
                    :limit="1"
                    accept=".xlsx,.xls"
                    :on-change="handleImportFileChange"
                    :on-remove="() => importFile = null"
                    drag
                    style="width: 100%;"
                >
                    <el-icon class="el-icon--upload"><Upload /></el-icon>
                    <div class="el-upload__text">
                        拖拽文件到此处，或 <em>点击上传</em>
                    </div>
                    <template #tip>
                        <div class="el-upload__tip" style="color: #94a3b8; font-size: 11px;">
                            文件必须包含“姓名”列，可选包含“学号”和“班级”列。
                        </div>
                    </template>
                </el-upload>
            </div>
            <template #footer>
                <el-button @click="importDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitStudentImport" :loading="importingStudents" :disabled="!importFile || !importForm.grade">
                    确定导入
                </el-button>
            </template>
        </el-dialog>

        <!-- 复用座位对话框 -->
        <el-dialog v-model="replicateDialogVisible" title="一键复制座位编排" width="550px" destroy-on-close>
            <div class="replicate-body">
                <p class="dialog-hint">将当前 <b>{{ selectedSession?.grade }} {{ selectedSession?.subject_name }}</b> 的考场座位分配一键复制应用到其他场次，确保这批学生所有的考试都在同一个座位：</p>
                <el-alert title="注意" type="warning" description="应用操作会清空目标场次已有的考场配置和座位表，并以当前场次的考场属性（座位数、考场类型）和对应的学生座次进行覆盖。" show-icon :closable="false" style="margin: 12px 0;" />
                
                <el-checkbox-group v-model="targetSessionIds" class="target-sessions-checklist">
                    <el-checkbox 
                        v-for="s in otherSessions" 
                        :key="s.id" 
                        :label="s.id" 
                        class="target-session-box"
                    >
                        {{ s.grade }} - {{ s.subject_name }} ({{ s.exam_date }} {{ s.start_time.substring(0, 5) }})
                    </el-checkbox>
                </el-checkbox-group>
            </div>
            <template #footer>
                <el-button @click="replicateDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitReplication" :loading="replicating" :disabled="targetSessionIds.length === 0">
                    确认复制应用
                </el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { semesterApi, studentSeatingApi, studentApi } from '@/api'
import { Download, Cpu, Grid, Upload } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'

const semesterId = ref(null)
const examId = ref(null)
const semesters = ref([])
const exams = ref([])
const sessions = ref([])

const selectedSession = ref(null)
const activeTab = ref('config')

// Tab 1: Config states
const roomConfigs = ref([])
const configLoading = ref(false)
const savingConfigs = ref(false)
const arrangementMode = ref('vertical_snake')
const arrangementStart = ref('right')

// Tab 2: Arrange stats
const arranging = ref(false)
const stats = reactive({
    total_students: 0,
    graded_count: 0,
    pass_count: 0,
    total_capacity: 0,
    unassigned_count: 0,
    unassigned_students: []
})

// Tab 3: Preview states
const previewMode = ref('room')
const previewRoomId = ref(null)
const previewClassName = ref(null)
const previewLoading = ref(false)
const seatMap = ref([])
const classSeatingList = ref([])
const allAssignments = ref([])

// Dialogue states
const replicateDialogVisible = ref(false)
const targetSessionIds = ref([])
const replicating = ref(false)

const exporting = ref(false)

// Helpers
const availableClasses = computed(() => {
    const classes = new Set(allAssignments.value.map(a => a.class_name).filter(Boolean))
    return Array.from(classes).sort()
})

const availableGrades = computed(() => {
    const grades = new Set(sessions.value.map(s => s.grade).filter(Boolean))
    return Array.from(grades).sort()
})

const otherSessions = computed(() => {
    if (!selectedSession.value) return []
    return sessions.value.filter(s => s.id !== selectedSession.value.id)
})

// Lifecycle
onMounted(async () => {
    await fetchSemesters()
})

// Functions
const fetchSemesters = async () => {
    try {
        semesters.value = await semesterApi.getAll()
        if (semesters.value.length > 0) {
            const active = semesters.value.find(s => s.is_active)
            semesterId.value = active ? active.id : semesters.value[0].id
            await fetchExams()
        }
    } catch (e) {
        console.error('获取学期失败', e)
    }
}

const fetchExams = async () => {
    if (!semesterId.value) return
    try {
        exams.value = await studentSeatingApi.getExams(semesterId.value)
        examId.value = null
        sessions.value = []
        selectedSession.value = null
    } catch (e) {
        console.error('获取考试列表失败', e)
    }
}

const handleSemesterChange = async () => {
    await fetchExams()
}

const handleExamChange = async () => {
    if (!examId.value) {
        sessions.value = []
        selectedSession.value = null
        return
    }
    await loadSessions()
}

const loadSessions = async () => {
    try {
        const exam = exams.value.find(e => e.id === examId.value)
        if (!exam) return
        
        // Use general exam fetch but we need session statistics
        const detailExam = await semesterApi.getById(semesterId.value) // Wait, we can query exams directly
        // Query sessions from exams details
        const allExams = await studentSeatingApi.getExams(semesterId.value)
        const currentExam = allExams.find(e => e.id === examId.value)
        
        // We need to fetch detailed sessions from the backend exams router
        const res = await semesterApi.getById(semesterId.value)
        // Let's call the exam list from backend
        const examsList = await studentSeatingApi.getExams(semesterId.value)
        // We will fetch the exact sessions in loadSessions from exams api
        const examDetail = await studentSeatingApi.getExams(semesterId.value)
        
        // Let's call exam endpoint directly
        const resList = await studentSeatingApi.getExams(semesterId.value)
        
        // To query actual sessions, we can pull them from exams.py endpoint response
        const activeExamData = await studentSeatingApi.getExams(semesterId.value)
        // Let's read sessions info
        // Wait, the API we created in student_seating.py is:
        // get_exams_progress returns total_sessions and assigned_sessions_count.
        // What about sessions? Let's check how we retrieve sessions.
        // In index.js: getExams pulls list of exams. We need their sessions.
        // Let's see: we can fetch exam by ID to get its sessions!
        // Yes, examApi.getById(id) returns detailed sessions!
        // Let's import examApi and fetch it.
        const responseDetail = await apiGetExamDetail(examId.value)
        sessions.value = responseDetail.sessions.map(s => {
            // Find if this session is already assigned in exams progress
            // We'll update the assigned_count based on seating assignments
            return {
                ...s,
                assigned_count: 0 // Will fetch later
            }
        })
        
        // Update assigned counts for each session
        for (let s of sessions.value) {
            const config = await studentSeatingApi.getSessionConfig(s.id)
            s.assigned_count = config.exam_rooms.reduce((acc, r) => acc + r.assigned_count, 0)
        }
        
        if (sessions.value.length > 0 && !selectedSession.value) {
            selectSession(sessions.value[0])
        }
    } catch (e) {
        console.error('加载场次列表失败', e)
    }
}

// Fetch helper mapping to existing examApi
const apiGetExamDetail = async (id) => {
    // In index.js examApi.getById is defined
    const { examApi } = await import('@/api')
    return await examApi.getById(id)
}

const selectSession = async (session) => {
    selectedSession.value = session
    activeTab.value = 'config'
    await loadSessionConfig()
}

const loadSessionConfig = async () => {
    if (!selectedSession.value) return
    configLoading.value = true
    try {
        const config = await studentSeatingApi.getSessionConfig(selectedSession.value.id)
        roomConfigs.value = config.exam_rooms
        arrangementMode.value = config.arrangement_mode || 'vertical_snake'
        arrangementStart.value = config.arrangement_start || 'right'
        
        // Calculate assigned count from config
        const totalAssigned = roomConfigs.value.reduce((acc, r) => acc + r.assigned_count, 0)
        selectedSession.value.assigned_count = totalAssigned
        
        // Load statistics for tab 2
        await loadSeatingStats()
    } catch (e) {
        console.error('加载考场配置失败', e)
    } finally {
        configLoading.value = false
    }
}

const loadSeatingStats = async () => {
    if (!selectedSession.value) return
    try {
        // Call arrange endpoint with empty execution to just get stats, or call the stats helper
        // Since stats is reactive, we can compute total_capacity, etc.
        stats.total_capacity = roomConfigs.value.reduce((acc, r) => acc + r.seat_capacity, 0)
        
        // Get all students of matching grade to calculate counts
        const { studentApi } = await import('@/api')
        const allStudents = await studentApi.getAll({ semester_id: semesterId.value, grade: selectedSession.value.grade })
        
        stats.total_students = allStudents.length
        
        const subject = selectedSession.value.subject_name
        stats.graded_count = allStudents.filter(stu => {
            const list = (stu.selected_subjects || '').split(',').map(s => s.trim()).filter(Boolean)
            return list.includes(subject)
        }).length
        
        stats.pass_count = stats.total_students - stats.graded_count
        
        // Update unassigned count
        if (selectedSession.value.assigned_count > 0) {
            stats.unassigned_count = Math.max(0, stats.total_students - selectedSession.value.assigned_count)
            // Load assignments for tab 3
            await loadAssignments()
        } else {
            stats.unassigned_count = 0
            stats.unassigned_students = []
        }
    } catch (e) {
        console.error('计算排考统计数据失败', e)
    }
}

const loadAssignments = async () => {
    if (!selectedSession.value) return
    try {
        const assignments = await studentSeatingApi.getAssignments(selectedSession.value.id)
        allAssignments.value = assignments
        
        // Update stats unassigned list if some students are left out
        if (stats.unassigned_count > 0) {
            const assignedIds = new Set(assignments.map(a => a.student_id))
            const { studentApi } = await import('@/api')
            const allStudents = await studentApi.getAll({ semester_id: semesterId.value, grade: selectedSession.value.grade })
            stats.unassigned_students = allStudents.filter(s => !assignedIds.has(s.id)).map(s => ({
                id: s.id,
                student_id: s.student_id,
                name: s.name,
                class_name: s.class_name,
                selected_subjects: s.selected_subjects
            }))
        } else {
            stats.unassigned_students = []
        }
    } catch (e) {
        console.error('加载座位分配列表失败', e)
    }
}

// 座位号从右上角开始，按列向下生成；蛇形模式每隔一列反向。
const buildSeatPositions = (capacity, mode, start) => {
    const numCols = 5
    const numRows = Math.ceil(capacity / numCols)
    const positions = []
    for (let colIndex = 0; colIndex < numCols; colIndex++) {
        const col = start === 'right' ? numCols - 1 - colIndex : colIndex
        const rows = []
        for (let row = 0; row < numRows; row++) {
            if (row * numCols + col < capacity) rows.push(row)
        }
        if (mode === 'vertical_snake' && colIndex % 2 === 1) rows.reverse()
        rows.forEach(row => positions.push({ row, col }))
    }
    return positions
}

const saveRoomConfigs = async () => {
    if (!selectedSession.value) return
    savingConfigs.value = true
    try {
        const configs = roomConfigs.value.map(r => ({
            exam_room_id: r.exam_room_id,
            seat_capacity: r.seat_capacity,
            room_type: r.room_type
        }))
        await studentSeatingApi.updateSessionConfig(selectedSession.value.id, configs)
        ElMessage.success('考场配置保存成功')
        await loadSessionConfig()
    } catch (e) {
        console.error('保存考场配置失败', e)
    } finally {
        savingConfigs.value = false
    }
}

const startArrange = async () => {
    if (!selectedSession.value) return
    
    // Save configs first before arranging
    await saveRoomConfigs()
    
    arranging.value = true
    try {
        const result = await studentSeatingApi.arrangeSession(selectedSession.value.id, arrangementMode.value, arrangementStart.value)
        ElMessage.success(`自动排座成功！已分配 ${result.assigned_count} 人。`)
        
        // Update UI
        selectedSession.value.assigned_count = result.assigned_count
        
        // Reload all data
        await loadSessionConfig()
        
        // Switch tab to preview
        activeTab.value = 'preview'
        previewMode.value = 'room'
        if (roomConfigs.value.length > 0) {
            previewRoomId.value = roomConfigs.value[0].exam_room_id
            await loadRoomSeatMap()
        }
    } catch (e) {
        console.error('自动编排失败', e)
    } finally {
        arranging.value = false
    }
}

const confirmClearSeating = () => {
    ElMessageBox.confirm(
        '此操作将永久清空该场次已编排的所有学生考场座位，是否确定清除？',
        '危险警告',
        {
            confirmButtonText: '确定清除',
            cancelButtonText: '取消',
            type: 'warning',
        }
    ).then(async () => {
        try {
            await studentSeatingApi.clearSession(selectedSession.value.id)
            ElMessage.success('座位编排已清除')
            selectedSession.value.assigned_count = 0
            await loadSessionConfig()
        } catch (e) {
            console.error('清除座位编排失败', e)
        }
    })
}

// Tab 3: Seating grid logic
const loadRoomSeatMap = async () => {
    if (!previewRoomId.value) return
    previewLoading.value = true
    try {
        await loadAssignments()
        
        const room = roomConfigs.value.find(r => r.exam_room_id === previewRoomId.value)
        const capacity = room ? room.seat_capacity : 30
        
        // Filter assignments for this room
        const roomAssignments = allAssignments.value.filter(a => a.exam_room_id === previewRoomId.value)
        const assignmentBySeat = {}
        roomAssignments.forEach(a => {
            assignmentBySeat[a.seat_number] = a
        })
        
        // Create full capacity array
        const positions = buildSeatPositions(capacity, arrangementMode.value, arrangementStart.value)
        const positionToSeat = new Map(positions.map((position, index) => [`${position.row}-${position.col}`, index + 1]))
        const list = []
        const numRows = Math.ceil(capacity / 5)
        for (let row = 0; row < numRows; row++) {
            // 网格按屏幕实际方向始终从左到右渲染；起始角只决定座位号落在哪一列。
            for (let col = 0; col < 5; col++) {
                const seatNumber = positionToSeat.get(`${row}-${col}`)
                if (!seatNumber) continue
                const asg = assignmentBySeat[seatNumber]
                list.push({
                    seat_number: seatNumber,
                    student_name: asg ? asg.student_name : null,
                    class_name: asg ? asg.class_name : null,
                    student_no: asg ? asg.student_no : null
                })
            }
        }
        seatMap.value = list
    } catch (e) {
        console.error('加载座位网格失败', e)
    } finally {
        previewLoading.value = false
    }
}

const loadClassSeatList = async () => {
    if (!previewClassName.value) return
    previewLoading.value = true
    try {
        await loadAssignments()
        classSeatingList.value = allAssignments.value.filter(a => a.class_name === previewClassName.value)
    } catch (e) {
        console.error('加载班级座位表失败', e)
    } finally {
        previewLoading.value = false
    }
}

const handleTabChange = async (tab) => {
    if (tab === 'preview') {
        if (roomConfigs.value.length > 0 && !previewRoomId.value) {
            previewRoomId.value = roomConfigs.value[0].exam_room_id
        }
        if (previewMode.value === 'room') {
            await loadRoomSeatMap()
        } else {
            await loadClassSeatList()
        }
    } else if (tab === 'arrange') {
        await loadSeatingStats()
    }
}

const getSeatClass = (seat) => {
    if (!seat.student_name) return 'seat-empty'
    const room = roomConfigs.value.find(r => r.exam_room_id === previewRoomId.value)
    if (!room) return 'seat-general'
    if (room.room_type === '等级考') return 'seat-graded'
    if (room.room_type === '合格考') return 'seat-pass'
    return 'seat-general'
}

// Tab 4: Excel Download handlers
const downloadExportBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
}

const exportSeatingCharts = async () => {
    if (!examId.value) return
    exporting.value = true
    try {
        const blob = await studentSeatingApi.exportSeating(examId.value)
        downloadExportBlob(blob, 'exam_seating_charts.xlsx')
        ElMessage.success('正在生成座位表并下载...')
    } catch (e) {
        console.error('导出座位图失败', e)
        ElMessage.error('导出失败')
    } finally {
        exporting.value = false
    }
}

const exportClassSeating = async () => {
    if (!examId.value) return
    exporting.value = true
    try {
        const blob = await studentSeatingApi.exportClassSeats(examId.value)
        downloadExportBlob(blob, 'class_seating_timetable.xlsx')
        ElMessage.success('正在生成班级对照表并下载...')
    } catch (e) {
        console.error('导出班级座位表失败', e)
        ElMessage.error('导出失败')
    } finally {
        exporting.value = false
    }
}

// Seating Replication dialog logic
const showReplicateDialog = () => {
    targetSessionIds.value = []
    replicateDialogVisible.value = true
}

const submitReplication = async () => {
    if (!selectedSession.value || targetSessionIds.value.length === 0) return
    replicating.value = true
    try {
        await studentSeatingApi.applySeatingToOthers(selectedSession.value.id, targetSessionIds.value)
        ElMessage.success('座位分配已一键应用到其他选定场次')
        replicateDialogVisible.value = false
        // Refresh session counts
        await loadSessions()
    } catch (e) {
        console.error('一键应用座位失败', e)
    } finally {
        replicating.value = false
    }
}

// Student Import states & methods
const importDialogVisible = ref(false)
const importingStudents = ref(false)
const importFile = ref(null)
const importUploadRef = ref(null)
const importForm = reactive({
    grade: ''
})

const showImportDialog = () => {
    importFile.value = null
    importForm.grade = selectedSession.value ? selectedSession.value.grade : (availableGrades.value[0] || '')
    importDialogVisible.value = true
}

const handleImportFileChange = (file) => {
    importFile.value = file.raw
}

const downloadStudentTemplate = async () => {
    try {
        const res = await studentSeatingApi.downloadStudentImportTemplate()
        const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const gradeName = importForm.grade || (selectedSession.value ? selectedSession.value.grade : '学生')
        link.setAttribute('download', `${gradeName}导入模板.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    } catch (e) {
        console.error('下载模板失败', e)
        ElMessage.error('下载模板失败')
    }
}

const submitStudentImport = async () => {
    if (!importFile.value || !importForm.grade) return
    importingStudents.value = true
    try {
        const result = await studentApi.batchImport(
            semesterId.value,
            importForm.grade,
            importFile.value
        )
        ElMessage.success(result.message || '导入成功')
        importDialogVisible.value = false
        // Reload seating statistics
        if (selectedSession.value) {
            await loadSeatingStats()
        }
    } catch (e) {
        console.error('导入学生失败', e)
    } finally {
        importingStudents.value = false
    }
}

const arrangingAll = ref(false)
const startArrangeAll = async () => {
    if (!examId.value) return
    arrangingAll.value = true
    try {
        const result = await studentSeatingApi.arrangeAllSessions(examId.value, arrangementMode.value, arrangementStart.value)
        ElMessage.success(result.message || '一键编排所有场次成功！')
        await loadSessions()
        if (selectedSession.value) {
            await loadSessionConfig()
        }
    } catch (e) {
        console.error('一键编排失败', e)
    } finally {
        arrangingAll.value = false
    }
}
</script>

<style scoped>
.exam-seats-management {
    max-width: 1400px;
    margin: 0 auto;
}

.filter-card {
    margin-bottom: 16px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
}

.filter-form {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.export-actions {
    margin-left: auto;
}

.workspace-container {
    display: flex;
    gap: 16px;
    height: calc(100vh - 200px);
    min-height: 550px;
}

.sessions-aside {
    width: 320px;
    flex-shrink: 0;
}

.aside-card {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.aside-header {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
}

.sessions-list {
    overflow-y: auto;
    max-height: calc(100vh - 270px);
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-right: 4px;
}

.session-item {
    padding: 12px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.session-item:hover {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.03);
    transform: translateY(-1px);
}

.session-item.is-active {
    border-color: #3b82f6;
    background: linear-gradient(90deg, rgba(59, 130, 246, 0.08), transparent);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.06);
}

.session-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.subj-grade {
    display: flex;
    align-items: center;
    gap: 8px;
}

.session-grade {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.session-subject {
    font-size: 13px;
    color: #475569;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
}

.session-time {
    font-size: 11px;
    color: #64748b;
}

.workspace-main {
    flex: 1;
    min-width: 0;
}

.main-card {
    height: 100%;
}

.main-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.grade-subj {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    margin-right: 8px;
}

.date-time {
    font-size: 13px;
    color: #475569;
}

.workspace-tabs {
    height: 100%;
}

.pane-content {
    padding-top: 8px;
    overflow-y: auto;
    max-height: calc(100vh - 290px);
}

.config-alert {
    margin-bottom: 16px;
}

.default-cap-hint {
    font-size: 11px;
    color: #94a3b8;
    margin-left: 8px;
}

.action-footer {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}

/* Arrange Pane styles */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;
}

.stat-box {
    text-align: center;
    padding: 16px 8px;
    border-radius: 10px;
}

.stat-num {
    font-size: 26px;
    font-weight: 800;
    margin-bottom: 6px;
}

.stat-label {
    font-size: 12px;
    color: #64748b;
}

.text-primary { color: #3b82f6; }
.text-success { color: #10b981; }
.text-warning { color: #f59e0b; }
.text-info { color: #06b6d4; }

.arrange-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin: 32px 0;
}

.arrangement-selector {
    flex-basis: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 4px;
}

.arrangement-label {
    color: #334155;
    font-size: 13px;
    font-weight: 600;
}

.arrangement-hint {
    color: #94a3b8;
    font-size: 12px;
}

.huge-btn {
    padding: 24px 38px;
    font-size: 16px;
    font-weight: 700;
    border-radius: 12px;
}

.shadow-btn {
    box-shadow: 0 4px 14px rgba(59, 130, 246, 0.4);
    transition: all 0.2s ease;
}

.shadow-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
}

.warning-section {
    margin-top: 24px;
    border-top: 1px dashed #e2e8f0;
    padding-top: 20px;
}

.unassigned-table-title {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin: 14px 0 8px 0;
}

/* Preview Seating Grid */
.preview-toolbar {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f1f5f9;
}

.room-seating-view {
    display: flex;
    justify-content: center;
}

.seating-grid-container {
    width: 100%;
    max-width: 900px;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: #fafafa;
    padding: 24px;
    border-radius: 16px;
    border: 1px solid #f1f5f9;
}

.blackboard {
    width: 300px;
    text-align: center;
    background: #475569;
    color: white;
    padding: 8px 0;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 36px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.seating-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;
    width: 100%;
}

.seat-desk {
    aspect-ratio: 1.5;
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    background: white;
    padding: 8px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.03);
    transition: all 0.2s ease;
}

.seat-desk:hover:not(.is-empty) {
    transform: scale(1.03);
    box-shadow: 0 4px 10px rgba(0,0,0,0.08);
}

.seat-badge {
    position: absolute;
    top: 4px;
    left: 6px;
    font-size: 9px;
    font-weight: 700;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 1px 4px;
    border-radius: 3px;
}

.seat-body {
    text-align: center;
    margin-top: 6px;
}

.student-name {
    font-size: 13px;
    font-weight: 700;
    color: #1f2937;
}

.student-class {
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
}

.empty-body {
    color: #cbd5e1;
    font-size: 11px;
    font-style: italic;
}

/* Desk Color Codes */
.seat-graded {
    background: rgba(16, 185, 129, 0.08);
    border-color: #10b981;
}
.seat-graded .student-name {
    color: #065f46;
}

.seat-pass {
    background: rgba(245, 158, 11, 0.08);
    border-color: #f59e0b;
}
.seat-pass .student-name {
    color: #92400e;
}

.seat-general {
    background: rgba(59, 130, 246, 0.08);
    border-color: #3b82f6;
}
.seat-general .student-name {
    color: #1e3a8a;
}

.seat-empty {
    background: #f8fafc;
    border-style: dashed;
}

/* Dialog checklists */
.target-sessions-checklist {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 250px;
    overflow-y: auto;
    margin-top: 16px;
    border: 1px solid #e2e8f0;
    padding: 12px;
    border-radius: 8px;
}

.target-session-box {
    margin: 0 !important;
    padding: 8px;
    border-radius: 4px;
}

.target-session-box:hover {
    background: #f8fafc;
}
</style>
